/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Tab,
  TabIndicator,
  TabList,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Field, Form, Formik, FormikProps } from 'formik';
import React, { BaseSyntheticEvent, useRef } from 'react';

import ConfirmationDialog from '@/components/confirmation-dialog/ConfirmationDialog';
import DepartmentsDropdown from '@/components/departments-dropdown/DepartmentsDropdown';
import TemplatesDropdown from '@/components/templates-dropdown/TemplatesDropdown';
import NavBlocker from '@/components/view-editor/NavBlocker';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { makeSelectTemplatesForSelectedDepartments } from '@/store/slices/departmentsAndTemplates.slice';
import {
  setViewEditorDraftName,
  setViewEditorDraftTemplateIds,
  setViewEditorDraftThemeDataLayoutDataType,
  setViewEditorShowConfirmRemoveDepartment,
} from '@/store/slices/viewEditor.slice';
import { RootState } from '@/store/store';
import { DropdownAction } from '@/types/ui.types';
import { ViewLayoutDataType, ViewLayoutType } from '@/types/view.types';
import viewEditorBasicPropertiesSchema from '@/utils/validationSchemas/viewEditorBasicProperties.validation.schema';

import ViewLayout from './ViewLayout';

interface ViewFormValues {
  viewName: string;
  departments: number[];
  templates: number[];
}

export interface ViewNameFieldProps {
  field: {
    name: string;
    value: string;
  };
  form: {
    errors: { [field: string]: string };
    touched: { [field: string]: boolean };
  };
}

export interface ViewDropdownFieldProps {
  field: {
    name: string;
    value: number[];
  };
  form: {
    errors: { [field: string]: string };
    touched: { [field: string]: boolean };
  };
}

interface BasicPropertiesProps {
  handleDepartmentOptionsChange: (selectedDepartmentIds: number[]) => void;
  handleTemplateOptionsChange: (selectedTemplateIds: number[]) => void;
}

const selectedTabStyle = {
  color: 'white',
};

const dataTypeTabIndices = Object.values(ViewLayoutDataType);

const ViewEditorBasicProperties = (props: BasicPropertiesProps): React.JSX.Element => {
  const { handleDepartmentOptionsChange, handleTemplateOptionsChange } = props;

  const {
    uiState: {
      selectedDepartments: selectedDepartmentIds,
      selectedTemplates: selectedTemplateIds,
      showConfirmRemoveDepartment,
    },
    viewDraft: view,
  } = useAppSelector((state) => state.viewEditor);

  const formikRef = useRef<FormikProps<ViewFormValues>>(null);

  const { departments } = useAppSelector((state) => state.departmentsAndTemplates);

  const templateForSelectedDepartmentsSelector = React.useMemo(() => makeSelectTemplatesForSelectedDepartments(), []);

  // Local copy of the selected departments to manage the confirmation modal when removing a department
  const [localSelectedDepartmentIds, setLocalSelectedDepartmentIds] = React.useState<number[]>([]);

  const selectTemplatesForSelectedDepartments = useAppSelector((state: RootState) =>
    templateForSelectedDepartmentsSelector(state, selectedDepartmentIds),
  );

  const dispatch = useAppDispatch();

  // eslint-disable-next-line no-magic-numbers
  if (!view || Object.keys(view).length === 0) {
    return <Box>View not found</Box>;
  }

  const handleTabSelectorChange = (index: number) => {
    const dataType = dataTypeTabIndices[index];

    dispatch(setViewEditorDraftThemeDataLayoutDataType(dataType));
  };

  const getDataTypeContainer = (): React.JSX.Element => {
    const selectedIndex = dataTypeTabIndices.indexOf(view.theme.data.dataType ?? ViewLayoutDataType.REQUEST);

    return (
      <VStack justifyContent={'space-between'} align={'left'}>
        <Text>Data Type</Text>
        <Tabs
          variant={'unstyled'}
          position={'relative'}
          index={selectedIndex}
          onChange={handleTabSelectorChange}
          isLazy
        >
          <TabList>
            <Tab _selected={selectedTabStyle}>Request</Tab>
            <Tab _selected={selectedTabStyle}>Schedule</Tab>
            <Tab _selected={selectedTabStyle}>Both</Tab>
          </TabList>
          <TabIndicator
            bg={'blue.500'}
            borderRadius={'10px'}
            height={'40px'}
            mt={'-40px'}
            zIndex={-1}
            position={'inherit'}
            textColor={'white'}
          />
        </Tabs>
      </VStack>
    );
  };

  const handleDepartmentChange = (departments: number[], action?: DropdownAction) => {
    // If the user is removing items (deselecting) or clearing (removing all items from the dropdown
    // show the confirmation dialog and save locally the new selected items
    // Otherwise, the user is selecting departments, and we just continue with the selection
    if (action === DropdownAction.DESELECT_OPTION || action === DropdownAction.CLEAR) {
      // Set the selected departments on the local copy
      setLocalSelectedDepartmentIds(departments);
      dispatch(setViewEditorShowConfirmRemoveDepartment(true));
    } else {
      handleDepartmentOptionsChange(departments);
      // Update department field value only if the user is selecting departments
      formikRef.current?.setFieldValue('departments', departments);
    }
  };

  // Close the modal and remove selected departments
  const handleCancelDepartmentRemoval = () => {
    dispatch(setViewEditorShowConfirmRemoveDepartment(false));
  };

  // Set selected departments based on the list of items selected on the dropdown
  const handleConfirmDepartmentRemoval = () => {
    handleDepartmentOptionsChange(localSelectedDepartmentIds);
    // Update department field value on confirmation
    formikRef.current?.setFieldValue('departments', localSelectedDepartmentIds);
    if (!localSelectedDepartmentIds.length) {
      dispatch(setViewEditorDraftTemplateIds([]));
      // Update templates field value on confirmation when we remove all the departments
      formikRef.current?.setFieldValue('templates', []);
    }
    handleCancelDepartmentRemoval();
  };

  return (
    <>
      <ConfirmationDialog
        closeButtonLabel="Cancel"
        closeConfirmDialog={() => dispatch(setViewEditorShowConfirmRemoveDepartment(false))}
        confirmButtonLabel="Confirm"
        description="Changing your selected departments may affect your Available Personnel and Assignments. Are you sure you want to make this change?"
        handleClose={handleCancelDepartmentRemoval}
        handleConfirm={handleConfirmDepartmentRemoval}
        isOpen={showConfirmRemoveDepartment}
        title="Warning"
      />
      <Box>
        <VStack align={'left'} justifyContent={'space-between'} gap={'5'}>
          <Formik
            innerRef={formikRef}
            initialValues={{
              departments: view.filter.on_departments,
              templates: view.filter.on_templates,
              viewName: view.name,
            }}
            onSubmit={() => {
              return;
            }}
            validationSchema={viewEditorBasicPropertiesSchema}
          >
            {({ setFieldValue, setTouched }) => (
              <Form>
                <Field name="viewName" value={view.name}>
                  {({ field, form }: ViewNameFieldProps) => (
                    <FormControl
                      onChange={(e: BaseSyntheticEvent) => dispatch(setViewEditorDraftName(e.target.value))}
                      isRequired
                      w={'100%'}
                      isInvalid={(form.errors.viewName && form.touched.viewName) as boolean}
                      onBlur={() =>
                        setTouched({
                          ...form.touched,
                          viewName: true,
                        })
                      }
                    >
                      <FormLabel>View Name</FormLabel>
                      <Input {...field} type={'text'} placeholder={'Enter a name for this view'}></Input>
                      <FormErrorMessage>{form.errors.viewName}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <HStack w={'min-content'} align={'flex-start'} mt={'20px'}>
                  <Field w={'auto'} name="departments">
                    {({ form }: ViewDropdownFieldProps) => (
                      <FormControl
                        isRequired
                        isInvalid={(form.errors.departments && form.touched.departments) as boolean}
                        onBlur={() =>
                          setTouched({
                            ...form.touched,
                            departments: true,
                          })
                        }
                      >
                        <DepartmentsDropdown
                          isRequired
                          departmentList={departments}
                          departmentChangeHandler={(values: number[], action?: DropdownAction) => {
                            handleDepartmentChange(values, action);
                          }}
                          selectedIds={selectedDepartmentIds}
                        />
                        <FormErrorMessage>{form.errors.departments}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field w={'auto'} name="templates">
                    {({ form }: ViewDropdownFieldProps) => (
                      <FormControl
                        isRequired
                        isInvalid={(form.errors.templates && form.touched.templates) as boolean}
                        onBlur={() =>
                          setTouched({
                            ...form.touched,
                            templates: true,
                          })
                        }
                      >
                        <TemplatesDropdown
                          isRequired
                          templateList={selectTemplatesForSelectedDepartments}
                          templateChangeHandler={(values: number[]) => {
                            handleTemplateOptionsChange(values);
                            setFieldValue('templates', values);
                          }}
                          selectedIds={selectedTemplateIds}
                        />
                        <FormErrorMessage>{form.errors.templates}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </HStack>
                <NavBlocker />
              </Form>
            )}
          </Formik>

          {view.theme.data.layout !== ViewLayoutType.BLOCK && getDataTypeContainer()}
          <ViewLayout />
        </VStack>
      </Box>
    </>
  );
};

export default ViewEditorBasicProperties;
